import { useEffect, useRef } from "react";

const noteStatusColor = (status) =>
    ({ planned: "bg-blue-100 text-blue-700", ongoing: "bg-amber-100 text-amber-700" }[status] || "");

const noteStatusLabel = (status) =>
    ({ planned: "Planerad", ongoing: "Pågående" }[status] || status);

const TypeDot = ({ noteTypes, type }) => {
    const color = noteTypes.find(nt => nt.name === type)?.color || "#9CA3AF";
    return <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />;
};

/* Small modal for selecting a tork.
   Shows each tork's name and its active (planned/ongoing) notes.
   Optional title/subtitle override the default header text. */
const sortTorks = (torks) =>
    torks.slice().sort((a, b) => {
        const aNum = Number(a.name);
        const bNum = Number(b.name);
        const aIsNum = !isNaN(aNum) && a.name.trim() !== "";
        const bIsNum = !isNaN(bNum) && b.name.trim() !== "";
        if (aIsNum && bIsNum) return aNum - bNum;
        if (aIsNum) return -1;
        if (bIsNum) return 1;
        return a.name.localeCompare(b.name, "sv");
    });

const TorkPicker = ({ torks, noteTypes, value, onChange, onClose, title = "Välj tork", subtitle }) => {
    const panelRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [onClose]);

    const select = (torkId) => {
        onChange(torkId || null);
        onClose();
    };

    return (
        /* Backdrop */
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
            {/* Modal panel */}
            <div
                ref={panelRef}
                className="bg-white rounded-lg shadow-xl border border-gray-200 w-[480px] max-w-full flex flex-col"
                style={{ maxHeight: "70vh" }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable tork list */}
                <div className="overflow-y-auto flex-1">
                    {/* Clear option */}
                    <button
                        className={
                            "w-full text-left px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-50 border-b " +
                            (!value ? "bg-blue-50 font-medium" : "")
                        }
                        onClick={() => select(null)}
                    >
                        – Ingen tork –
                    </button>

                    {sortTorks(torks).map(t => {
                        const activeNotes = (t.notes || []).filter(
                            n => n.status === "planned" || n.status === "ongoing"
                        );
                        const isSelected = t.id === value;

                        return (
                            <button
                                key={t.id}
                                className={
                                    "w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b last:border-b-0 " +
                                    (isSelected ? "bg-blue-50" : "")
                                }
                                onClick={() => select(t.id)}
                            >
                                {/* Tork name row */}
                                <div className="flex items-center gap-1.5 font-medium text-gray-800">
                                    {activeNotes.length > 0 && (
                                        <span className="text-amber-500 text-xs" title="Aktiva noteringar">⚠</span>
                                    )}
                                    {t.name}
                                </div>

                                {/* Active notes */}
                                {activeNotes.length > 0 ? (
                                    <ul className="mt-1 space-y-0.5">
                                        {activeNotes.map(n => (
                                            <li key={n.id} className="flex items-start gap-1.5 text-xs text-gray-600">
                                                <TypeDot noteTypes={noteTypes} type={n.type} />
                                                <span className="flex-1">{n.type} – {n.note}</span>
                                                <span className={"px-1.5 py-0.5 rounded text-xs " + noteStatusColor(n.status)}>
                                                    {noteStatusLabel(n.status)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="mt-0.5 text-xs text-gray-400">Inga aktiva noteringar</p>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default TorkPicker;
